import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { get } from 'lodash'
import config from '@src/config'
import validateHvacZipCode from '../../utils/validateHvacZipCode'

import ErrorMessage from './ErrorMessage'
import { useFormContext } from '@context/FormContext'

const Address = ({ name, alt, label, setAddressValid, setIsWellesleyTown, setIsMaAddress, setValidZip, autocomplete: autoCompleteProp = 'off', ...props }) => {
  const { values, errors, submitCount, setFieldValue } = useFormContext()
  
  const fieldValue = get(values, name) || ''
  const fieldError = get(errors, name)
  // Only show errors after user tries to submit (submitCount > 0)
  const fieldTouched = submitCount > 0

  const autocomplete = useRef(null)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  const handleChange = (e) => {
    if (e.target.name === 'contactInfo.address') {
      setAddressValid(false)
    }
    setFieldValue(name, e.target.value)
  }

  // handleAddressComponents takes in an array of objects returned from the google autocomplete api
  const handleAddressComponents = (components) => {
    const addressComponents = {}
    for (let i = 0; i < components.length; i++) {
      addressComponents[components[i].types[0]] = components[i].long_name
    }
    return addressComponents
  }

  const handlePlaceSelect = async () => {
    const place = autocomplete.current.getPlace()
    let isMaAddress = false
    let isWellesleyTown = false
    let isValid = false
    
    if (!place || !place.address_components) {
      return alert(
        'Error retrieving address information from Google.\n\nPlease try again later or call a HomeWorks Energy Specialist at (781) 305-3319.'
      )
    }
    
    for (let k = 0; k < place.address_components.length; k++) {
      const comp = place.address_components[k]
      const { long_name, short_name, types } = comp
      
      if (config.dataSharingTowns.includes(long_name)) {
        isWellesleyTown = true
      }
      if (long_name === 'Massachusetts' && short_name === 'MA') {
        isMaAddress = true
      }
      if (values?.path === 'hvac' && types.includes('postal_code')) {
        const { short_name } = comp
        const valid = await validateHvacZipCode(short_name)
        isValid = valid
      }
    }
    
    setIsWellesleyTown(isWellesleyTown)
    if (!isMaAddress) {
      return setIsMaAddress(isMaAddress)
    }
    if (values?.path === 'hvac' && !isValid) {
      setValidZip(true)
      return setValidZip(isValid)
    }
    
    setAddressValid(true)
    setFieldValue(name, place.formatted_address)
    if (values?.path === 'hvac' && alt) {
      setFieldValue(alt, handleAddressComponents(place.address_components))
    }
  }

  const repositionDropdown = () => {
    dropdownRef.current = window.document.querySelector('body > .pac-container')
    if (inputRef.current && dropdownRef.current) {
      inputRef.current.parentNode.appendChild(dropdownRef.current)
    }
  }

  useEffect(() => {
    if (inputRef.current && window.google?.maps?.places) {
      autocomplete.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      )
      autocomplete.current.addListener('place_changed', handlePlaceSelect)
      if (inputRef.current.value) setAddressValid(true)
    }
  }, [])

  useEffect(() => {
    repositionDropdown()
  })

  return (
    <div
      className={classNames(
        'mb-4',
        { 'opacity-60': props.disabled },
        { 'has-error': fieldError && fieldTouched },
        { 'has-success': !fieldError && fieldTouched },
      )}
    >
      {label && (
        <label className="form-label" htmlFor={name}>
          {label}
        </label>
      )}
      
      <input
        {...props}
        id={name}
        name={name}
        className="form-input"
        disabled={props.disabled}
        onChange={handleChange}
        placeholder={props.placeholder || ''}
        ref={inputRef}
        type="search"
        value={fieldValue}
      />

      <ErrorMessage render={!!(fieldError && fieldTouched)} message={fieldError} />
    </div>
  )
}

Address.propTypes = {
  name: PropTypes.string.isRequired,
  alt: PropTypes.string,
  autocomplete: PropTypes.string,
  autofocus: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  setAddressValid: PropTypes.func.isRequired,
  setIsWellesleyTown: PropTypes.func.isRequired,
  setIsMaAddress: PropTypes.func.isRequired,
  setValidZip: PropTypes.func.isRequired,
}

export default Address
